/**
 * PaymentPage — выбор абонемента или разового занятия и редирект на ЮKassa.
 * Маршрут: /cabinet/students/:id/topup
 *
 * Все цены загружаются из API (никакого хардкода). Семейная скидка
 * применяется и к абонементам, и к разовым занятиям.
 */
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Stack, Box, Text, Title, Paper, Button, Loader, Center,
  SimpleGrid, ActionIcon, Group,
} from '@mantine/core';
import {
  IconArrowLeft, IconCreditCard, IconCheck, IconDiscount2,
  IconPlus, IconMinus, IconAlertTriangle,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import CabinetLayout from '../components/CabinetLayout';
import { cabinetService } from '../services/api';
import tokens from '../theme/designTokens';

const formatRub = (val) =>
  Number(val).toLocaleString('ru-RU', { maximumFractionDigits: 0 }) + ' ₽';

const PRESET_LABELS = ['Стандарт', 'Двойной', 'Тройной'];

// Применить скидку (округление до целого рубля, как на backend)
const applyDiscount = (amount, discountPercent) => {
  if (!discountPercent || discountPercent <= 0) return Math.round(Number(amount));
  const factor = (100 - Number(discountPercent)) / 100;
  return Math.round(Number(amount) * factor);
};

const PaymentPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [student, setStudent] = useState(null);
  const [presetsData, setPresetsData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [maintenanceMode, setMaintenanceMode] = useState(false);

  // Выбор: 'subscription' | 'single'
  const [mode, setMode] = useState('subscription');
  const [selectedPreset, setSelectedPreset] = useState(0);
  const [singleCount, setSingleCount] = useState(1);

  useEffect(() => {
    let mounted = true;
    Promise.all([
      cabinetService.getMyStudents(),
      cabinetService.getPaymentPresets(id),
      cabinetService.getPaymentMode().catch(() => ({ payments_available: true, maintenance_mode: false })),
    ])
      .then(([studentsData, presets, paymentMode]) => {
        if (!mounted) return;
        const items = studentsData.items || studentsData;
        const found = items.find((s) => s.id === Number(id));
        if (found) setStudent(found);
        else {
          notifications.show({
            title: 'Ошибка',
            message: 'Ребёнок не найден',
            color: 'red',
          });
          navigate('/');
          return;
        }
        setPresetsData(presets);
        setMaintenanceMode(paymentMode.maintenance_mode);
      })
      .catch((e) => mounted && notifications.show({
        title: 'Ошибка', message: e.message, color: 'red',
      }))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [id, navigate]);

  const handlePay = async () => {
    setSubmitting(true);
    try {
      let payload;
      if (mode === 'subscription') {
        const preset = presetsData.presets[selectedPreset];
        payload = {
          amount: Number(preset.final_amount),
          lessons_count: preset.lessons_count,
          payment_kind: 'subscription',
        };
      } else {
        const count = Math.max(1, Number(singleCount) || 1);
        const baseTotal = Number(presetsData.single_lesson_price) * count;
        const discounted = applyDiscount(
          baseTotal,
          presetsData.discount_active ? presetsData.family_discount_percent : 0,
        );
        payload = {
          amount: discounted,
          lessons_count: count,
          payment_kind: 'single_lessons',
        };
      }

      const response = await cabinetService.createTopup(id, payload);
      if (response.confirmation_url) {
        window.location.href = response.confirmation_url;
      } else {
        throw new Error('Не получен URL для оплаты');
      }
    } catch (err) {
      notifications.show({ title: 'Ошибка', message: err.message, color: 'red' });
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <CabinetLayout>
        <Center py={80}><Loader color="brand-purple" /></Center>
      </CabinetLayout>
    );
  }

  const presets = presetsData?.presets || [];
  const discountActive = presetsData?.discount_active;
  const discountPercent = Number(presetsData?.family_discount_percent || 0);
  const minChildren = presetsData?.family_discount_min_children;
  const singleLessonBase = Number(presetsData?.single_lesson_price || 0);

  // Расчёт стоимости разовых занятий
  const singleBaseTotal = singleLessonBase * singleCount;
  const singleFinalTotal = applyDiscount(
    singleBaseTotal,
    discountActive ? discountPercent : 0,
  );
  const singleFinalPerLesson = applyDiscount(
    singleLessonBase,
    discountActive ? discountPercent : 0,
  );

  return (
    <CabinetLayout>
      <Stack gap="lg">
        <Button
          variant="subtle"
          color="brand-purple"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => navigate('/')}
          style={{ alignSelf: 'flex-start' }}
        >
          Назад
        </Button>

        <Box>
          <Title
            order={2}
            style={{
              fontFamily: tokens.typography.fontFamilyHeading,
              color: tokens.colors.text.primary,
            }}
          >
            Пополнение баланса
          </Title>
          <Text c={tokens.colors.text.muted} fw={700} mt={4}>
            {student && [student.first_name, student.last_name].filter(Boolean).join(' ')}
          </Text>
        </Box>

        {/* Баннер тех. работ */}
        {maintenanceMode && (
          <Paper
            p="md"
            radius="xl"
            style={{
              background: 'linear-gradient(135deg, #FFF3E0, #FFE0B2)',
              border: '2px solid rgba(255,152,0,0.3)',
            }}
          >
            <Group gap="sm" align="center">
              <IconAlertTriangle size={22} color="#E65100" stroke={2.5} />
              <Box>
                <Text fw={900} style={{ color: '#E65100' }}>
                  Технические работы
                </Text>
                <Text size="sm" c={tokens.colors.text.muted} fw={700}>
                  Оплата временно недоступна. Попробуйте позже.
                </Text>
              </Box>
            </Group>
          </Paper>
        )}

        {/* Плашка с семейной скидкой */}
        {discountActive && (
          <Paper
            p="md"
            radius="xl"
            style={{
              background: tokens.gradients.softGreen,
              border: `2px solid ${tokens.colors.brand.green}33`,
            }}
          >
            <Box style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Box
                style={{
                  width: 44,
                  height: 44,
                  borderRadius: 14,
                  background: 'rgba(22,193,114,0.15)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: tokens.colors.brand.green,
                  flexShrink: 0,
                }}
              >
                <IconDiscount2 size={24} stroke={2.5} />
              </Box>
              <Box style={{ flex: 1 }}>
                <Text
                  fw={900}
                  style={{
                    fontFamily: tokens.typography.fontFamilyHeading,
                    color: tokens.colors.text.primary,
                  }}
                >
                  Семейная скидка −{discountPercent}%
                </Text>
                <Text size="sm" c={tokens.colors.text.muted} fw={700}>
                  Применяется автоматически — у вас {presetsData.active_children_count}+ детей
                  {minChildren ? ` (от ${minChildren})` : ''}
                </Text>
              </Box>
            </Box>
          </Paper>
        )}

        {/* Один блок с плитками: 3 абонемента + 1 разовое */}
        {(presets.length > 0 || singleLessonBase > 0) && (
          <Paper p="xl" radius="xl" style={{ background: 'white' }}>
            <Stack gap="md">
              <Title order={4} style={{ color: tokens.colors.text.primary }}>
                Выберите вариант оплаты
              </Title>

              <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                {/* Плитки абонементов */}
                {presets.map((p, idx) => {
                  const isSelected = mode === 'subscription' && selectedPreset === idx;
                  const baseAmount = Number(p.base_amount);
                  const finalAmount = Number(p.final_amount);
                  const hasDiscount = baseAmount > finalAmount;
                  return (
                    <Box
                      key={idx}
                      onClick={() => { setMode('subscription'); setSelectedPreset(idx); }}
                      style={{
                        cursor: 'pointer',
                        padding: 20,
                        borderRadius: 20,
                        background: isSelected ? tokens.gradients.softPurple : tokens.colors.bg.page,
                        border: `3px solid ${isSelected ? tokens.colors.brand.purple : 'transparent'}`,
                        transition: 'all 0.2s',
                        position: 'relative',
                      }}
                    >
                      {isSelected && (
                        <Box
                          style={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            background: tokens.colors.brand.purple,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                          }}
                        >
                          <IconCheck size={14} />
                        </Box>
                      )}
                      <Text size="xs" fw={800} tt="uppercase" c={tokens.colors.text.muted}>
                        {PRESET_LABELS[idx] || `Пакет ${idx + 1}`}
                      </Text>
                      <Text
                        style={{
                          fontFamily: tokens.typography.fontFamilyHeading,
                          fontWeight: 1000,
                          fontSize: 28,
                          color: tokens.colors.text.primary,
                          lineHeight: 1.1,
                          marginTop: 4,
                        }}
                      >
                        {p.lessons_count} занятий
                      </Text>
                      <Box mt="xs" style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                        <Text fw={900} style={{ color: tokens.colors.brand.orange, fontSize: 20 }}>
                          {formatRub(finalAmount)}
                        </Text>
                        {hasDiscount && (
                          <Text
                            size="sm"
                            fw={700}
                            style={{
                              color: tokens.colors.text.muted,
                              textDecoration: 'line-through',
                            }}
                          >
                            {formatRub(baseAmount)}
                          </Text>
                        )}
                      </Box>
                      <Text size="xs" c={tokens.colors.text.muted} fw={700} mt={4}>
                        по {formatRub(finalAmount / p.lessons_count)} за занятие
                      </Text>
                    </Box>
                  );
                })}

                {/* Плитка разового занятия */}
                {singleLessonBase > 0 && (() => {
                  const isSelected = mode === 'single';
                  const hasDiscount = singleBaseTotal > singleFinalTotal;
                  return (
                    <Box
                      onClick={() => setMode('single')}
                      style={{
                        cursor: 'pointer',
                        padding: 20,
                        borderRadius: 20,
                        background: isSelected ? tokens.gradients.softPurple : tokens.colors.bg.page,
                        border: `3px solid ${isSelected ? tokens.colors.brand.purple : 'transparent'}`,
                        transition: 'all 0.2s',
                        position: 'relative',
                      }}
                    >
                      {isSelected && (
                        <Box
                          style={{
                            position: 'absolute',
                            top: 12,
                            right: 12,
                            width: 24,
                            height: 24,
                            borderRadius: '50%',
                            background: tokens.colors.brand.purple,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                          }}
                        >
                          <IconCheck size={14} />
                        </Box>
                      )}
                      <Text size="xs" fw={800} tt="uppercase" c={tokens.colors.text.muted}>
                        Разовое занятие
                      </Text>
                      <Box
                        mt={4}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          paddingRight: 28,
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ActionIcon
                          size="sm"
                          radius="xl"
                          variant="filled"
                          color="brand-purple"
                          onClick={() => { setMode('single'); setSingleCount((c) => Math.max(1, c - 1)); }}
                          disabled={singleCount <= 1}
                        >
                          <IconMinus size={14} />
                        </ActionIcon>
                        <Text
                          style={{
                            fontFamily: tokens.typography.fontFamilyHeading,
                            fontWeight: 1000,
                            fontSize: 28,
                            color: tokens.colors.text.primary,
                            lineHeight: 1.1,
                            minWidth: 130,
                          }}
                        >
                          {singleCount} {singleCount === 1 ? 'занятие' : 'занятий'}
                        </Text>
                        <ActionIcon
                          size="sm"
                          radius="xl"
                          variant="filled"
                          color="brand-purple"
                          onClick={() => { setMode('single'); setSingleCount((c) => c + 1); }}
                        >
                          <IconPlus size={14} />
                        </ActionIcon>
                      </Box>
                      <Box mt="xs" style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                        <Text fw={900} style={{ color: tokens.colors.brand.orange, fontSize: 20 }}>
                          {formatRub(singleFinalTotal)}
                        </Text>
                        {hasDiscount && (
                          <Text
                            size="sm"
                            fw={700}
                            style={{
                              color: tokens.colors.text.muted,
                              textDecoration: 'line-through',
                            }}
                          >
                            {formatRub(singleBaseTotal)}
                          </Text>
                        )}
                      </Box>
                      <Text size="xs" c={tokens.colors.text.muted} fw={700} mt={4}>
                        по {formatRub(singleFinalPerLesson)} за занятие
                      </Text>
                    </Box>
                  );
                })()}
              </SimpleGrid>
            </Stack>
          </Paper>
        )}

        {/* Кнопка оплаты */}
        <Button
          size="xl"
          color="brand-orange"
          leftSection={<IconCreditCard size={20} />}
          loading={submitting}
          onClick={handlePay}
          fullWidth
          disabled={(presets.length === 0 && singleLessonBase === 0) || maintenanceMode}
        >
          Оплатить
        </Button>

        <Text size="xs" c={tokens.colors.text.muted} fw={700} ta="center">
          Вы будете перенаправлены на защищённую страницу ЮKassa.
          После успешной оплаты вам придёт чек на email.
        </Text>
      </Stack>
    </CabinetLayout>
  );
};

export default PaymentPage;
